import {Action, Module, Mutation} from 'vuex-module-decorators';
import store from '@/store';
import {InvitationMode, InvitationsModel, InvitationStatus, TeamModel} from '@/models/team.model';
import {ChallengeTeamsApi} from '@/api/challenge-teams.api';
import {take} from 'rxjs/operators';
import {EntityStateAdapter} from '@/store/base.store';
import {UserModel} from '@/models/user.model';

export const featureKey = 'teams';

// @ts-ignore
if (store.state.teams) {
  store.unregisterModule(featureKey)
}

@Module({
  store,
  dynamic: true,
  name: featureKey,
  namespaced: true,
})
export class TeamsStoreModule extends EntityStateAdapter<TeamModel> {

  private _selectedTeam: TeamModel | null = null;
  private _usersToInvite: number[] = [];
  private _invites: InvitationsModel[] = [];

  get teams(): TeamModel[] {
    return super.entities;
  }

  get selectedTeam(): TeamModel | null {
    return this._selectedTeam;
  }

  get usersToInvite(): number[] {
    return this._usersToInvite;
  }

  get invites(): InvitationsModel[] {
    return this._invites;
  }

  get invitesByMode(): (mode: InvitationMode) => InvitationsModel[] {
    return (mode: InvitationMode) => this._invites.filter((v) => v.invitation_mode === mode)
  }

  get invitesByStatus(): (status: InvitationStatus) => InvitationsModel[] {
    return (status: InvitationStatus) => this._invites.filter((v) => v.status === status)
  }

  get invitesByModeAndStatuses(): (mode: InvitationMode, status: InvitationStatus) => InvitationsModel[] {
    return (mode, status) => this._invites.filter((v) => v.status === status && v.invitation_mode === mode)
  }

  get isOwner(): (team: TeamModel, user: UserModel) => boolean {
    return (team: TeamModel, user: UserModel) => team?.responsible?.id === user?.id
  }

  @Mutation
  changeInviteStatus(data: { inviteId: number, status: InvitationStatus }): void {
    const index = this._invites.findIndex((v) => v.id === data.inviteId);
    if (~index) {
      this._invites[index].status = data.status;
    }
  }

  @Mutation
  upsertUserInvitation(userId: number): void {
    const index = this._usersToInvite.findIndex((v) => v === userId);
    if (~index) {
      this._usersToInvite.splice(index, 1)
    } else {
      this._usersToInvite.push(userId)
    }
  }

  @Mutation
  pendentInvitesLoaded(invites: InvitationsModel[]) {
    this._invites = invites;
  }

  @Mutation
  teamLoaded(team: TeamModel | null) {
    this._selectedTeam = team;
    if (team) {
      super.upsertOne(team)
    }
  }

  @Mutation
  myTeamLoaded(team: TeamModel) {
    this._selectedTeam = team;
    if (team) {
      super.upsertOne(team)
    }
  }

  @Action({commit: 'insertMany', rawError: true})
  async fetchTeams(challengeId: number): Promise<TeamModel[]> {
    if (!!this.entities && this.entities.length > 0) {
      return this.entities;
    }
    return await (new ChallengeTeamsApi(challengeId))
      .all()
      .pipe(take(1))
      .toPromise();
  }

  @Action({commit: 'upsertOne', rawError: true})
  async updateTeam(data: { challengeId: number, team: TeamModel }): Promise<TeamModel> {
    const team: TeamModel = {...data.team};
    if (this.usersToInvite.length > 0) {
      team.invitations = this.usersToInvite
    }
    return await (new ChallengeTeamsApi(data.challengeId))
      .update(team)
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'insertOne', rawError: true})
  async createTeam(data: { challengeId: number, team: TeamModel }): Promise<TeamModel> {
    const team: TeamModel = {...data.team};
    if (this.usersToInvite.length > 0) {
      team.invitations = this.usersToInvite
    }
    return await (new ChallengeTeamsApi(data.challengeId))
      .create(team)
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'removeOne', rawError: true})
  async deleteTeam(data: { challengeId: number, teamId: number }): Promise<number> {
    await (new ChallengeTeamsApi(data.challengeId))
      .destroy(data.teamId)
      .pipe(take(1))
      .toPromise();
    return data.teamId
  }

  @Action({rawError: true, commit: 'teamLoaded'})
  async fetchTeam(data: { challengeId: number, teamId: number }): Promise<TeamModel> {
    return await (new ChallengeTeamsApi(data.challengeId))
      .findById(data.teamId)
      .pipe(take(1))
      .toPromise()
  }

  @Action({rawError: true, commit: 'teamLoaded'})
  async fetchTeamBySlug(data: { challengeId: number, slug: string }): Promise<TeamModel> {
    return await (new ChallengeTeamsApi(data.challengeId))
      .fetchBySlug(data.slug)
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'changeInviteStatus', rawError: true})
  async leaveTeam(data: { challengeId: number, teamId: number, inviteId: number })
    : Promise<{ inviteId: number, status: InvitationStatus }> {
    await (new ChallengeTeamsApi(data.challengeId))
      .leaveTeam(data.teamId, data.inviteId)
      .pipe(take(1))
      .toPromise();
    return {inviteId: data.inviteId, status: 'removed'};
  }

  @Action
  async sendInvitation(data: { challengeId: number, teamId: number }): Promise<any> {
    return await (new ChallengeTeamsApi(data.challengeId))
      .sendInvitation(data.teamId)
      .pipe(take(1))
      .toPromise();
  }

  @Action({commit: 'upsertUserInvitation', rawError: true})
  onUpsertUserInvitation(userId: number): number {
    return userId
  }

  @Action
  async onInviteUsers(data: { challengeId: number, teamId: number }): Promise<any> {
    return await new ChallengeTeamsApi(data.challengeId)
      .sendInvitations(data.teamId, this.usersToInvite)
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'myTeamLoaded', rawError: true})
  async fetchMyTeam(challengeId: number, userId: number): Promise<TeamModel> {
    return await new ChallengeTeamsApi(challengeId)
      .userTeam(challengeId, userId).pipe(take(1)).toPromise()
  }

  @Action({commit: 'pendentInvitesLoaded', rawError: true})
  async fetchPendingInvites(data: { challengeId: number, slug: string }): Promise<InvitationsModel[]> {
    return await new ChallengeTeamsApi(data.challengeId)
      .fetchPendentInvitations(data.slug)
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'changeInviteStatus', rawError: true})
  async approveInvite(data: { challengeId: number, teamId: number, inviteId: number })
    : Promise<{ inviteId: number, status: InvitationStatus }> {
    await new ChallengeTeamsApi(data.challengeId)
      .acceptInvitation(data.teamId, data.inviteId)
      .pipe(take(1))
      .toPromise();
    return {status: 'accepted', inviteId: data.inviteId}
  }

  @Action({commit: 'changeInviteStatus', rawError: true})
  async refuseInvite(data: { challengeId: number, teamId: number, inviteId: number })
    : Promise<{ inviteId: number, status: InvitationStatus }> {
    await new ChallengeTeamsApi(data.challengeId)
      .refuseInvitation(data.teamId, data.inviteId)
      .pipe(take(1))
      .toPromise();
    return {status: 'rejected', inviteId: data.inviteId}
  }

  @Action({commit: 'changeInviteStatus', rawError: true})
  async cancelInvite(data: { challengeId: number, teamId: number, inviteId: number })
    : Promise<{ inviteId: number, status: InvitationStatus }> {
    await new ChallengeTeamsApi(data.challengeId)
      .cancelInvitation(data.teamId, data.inviteId)
      .pipe(take(1))
      .toPromise();
    return {status: 'canceled', inviteId: data.inviteId}
  }
}
